// The camera grade (docs/19) as one filter pass. Phaser runs each camera filter as its own
// full-screen pass into its own framebuffer: the hour's colour matrix and then the vignette were two
// passes over every screen pixel, every frame (on a HiDPI laptop, over five million pixels each).
// This is the same two shaders, Phaser's own, run back to back in one pass: the colour matrix on
// the camera's pixel, then the vignette's mix over the result. The only difference is that the
// matrix's output is no longer rounded to 8 bits before the vignette reads it.
import Phaser from 'phaser';

const FRAG = `
#pragma phaserTemplate(shaderName)
precision mediump float;
uniform sampler2D uMainSampler;
uniform float uColorMatrix[20];
uniform float uAlpha;
uniform float uRadius;
uniform float uStrength;
uniform vec2 uPosition;
uniform vec4 uColor;
varying vec2 outTexCoord;
vec4 colorMatrix(vec4 c)
{
    if (uAlpha == 0.0) return c;
    if (c.a > 0.0) c.rgb /= c.a;
    vec4 result;
    result.r = (uColorMatrix[0] * c.r) + (uColorMatrix[1] * c.g) + (uColorMatrix[2] * c.b) + (uColorMatrix[3] * c.a) + uColorMatrix[4];
    result.g = (uColorMatrix[5] * c.r) + (uColorMatrix[6] * c.g) + (uColorMatrix[7] * c.b) + (uColorMatrix[8] * c.a) + uColorMatrix[9];
    result.b = (uColorMatrix[10] * c.r) + (uColorMatrix[11] * c.g) + (uColorMatrix[12] * c.b) + (uColorMatrix[13] * c.a) + uColorMatrix[14];
    result.a = (uColorMatrix[15] * c.r) + (uColorMatrix[16] * c.g) + (uColorMatrix[17] * c.b) + (uColorMatrix[18] * c.a) + uColorMatrix[19];
    vec3 rgb = mix(c.rgb, result.rgb, uAlpha);
    rgb *= result.a;
    return clamp(vec4(rgb, result.a), 0.0, 1.0);
}
void main ()
{
    vec4 texture = colorMatrix(texture2D(uMainSampler, outTexCoord));
    float vignette = 1.0;
    vec2 position = vec2(uPosition.x, 1.0 - uPosition.y);
    float d = length(outTexCoord - position);
    if (d <= uRadius)
    {
        float g = d / uRadius;
        vignette = sin(g * 3.14 * uStrength);
    }
    gl_FragColor = mix(texture, uColor, vignette);
}
`;

const NODE = 'FilterFkGrade';

interface GradeController { colorMatrix: Phaser.Display.ColorMatrix; radius: number; strength: number; x: number; y: number; color: Phaser.Display.Color }

class FilterFkGrade extends Phaser.Renderer.WebGL.RenderNodes.BaseFilterShader {
  constructor(manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager) { super(NODE, manager, undefined, FRAG); }

  override setupUniforms(controller: Phaser.Filters.Controller): void {
    const c = controller as unknown as GradeController, pm = this.programManager, k = c.color;
    pm.setUniform('uColorMatrix[0]', c.colorMatrix.getData());
    pm.setUniform('uAlpha', c.colorMatrix.alpha);
    pm.setUniform('uRadius', c.radius);
    pm.setUniform('uStrength', c.strength);
    pm.setUniform('uPosition', [c.x, c.y]);
    pm.setUniform('uColor', [k.redGL, k.greenGL, k.blueGL, k.alphaGL]);
  }
}

/** The grade: `colorMatrix` as Phaser's ColorMatrix filter has it, the vignette as `addVignette(x, y, radius, strength, color)` (NORMAL blend). */
export class Grade extends Phaser.Filters.Controller {
  readonly colorMatrix = new Phaser.Display.ColorMatrix();
  readonly color = new Phaser.Display.Color();
  constructor(camera: Phaser.Cameras.Scene2D.Camera, public x: number, public y: number, public radius: number, public strength: number, color: number) {
    super(camera, NODE);
    Phaser.Display.Color.IntegerToColor(color, this.color);
  }
}

/**
 * Add the grade to a camera's external filters (the render node is registered with the renderer the
 * first time). docs/147 PG6: as the camera's last external filter at full size, Phaser draws it
 * straight onto the screen; as an internal filter it went into a framebuffer of its own and was then
 * copied onto the screen, one more full-screen pass for the same pixels (no padding, no transform:
 * the camera's framebuffer and the screen are the same size and lie on each other).
 */
export function addGrade(cam: Phaser.Cameras.Scene2D.Camera, x: number, y: number, radius: number, strength: number, color: number): Grade {
  const nodes = (cam.scene.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer).renderNodes;
  if (!nodes.hasNode(NODE)) nodes.addNodeConstructor(NODE, FilterFkGrade);
  return cam.filters!.external.add(new Grade(cam, x, y, radius, strength, color)) as Grade;
}
